import { useEffect, useRef, useState } from "react";
import * as Accordion from "@radix-ui/react-accordion";
import { Link, useNavigate, useParams } from "react-router";
import { useKit } from "@/api/useKit";
import { useBrandStore } from "@/state/brandStore";
import { useMyBrandProfile } from "@/api/mutations";
import { useUpdateBrandProfile } from "@/api/adminMutations";
import { BoxMockup } from "@/components/BoxMockup";
import { LabFormMockup } from "@/components/LabFormMockup";
import { ColourPicker } from "@/components/ColourPicker";
import { LogoDropzone } from "@/components/LogoDropzone";
import { Stepper } from "@/components/Stepper";
import { HelpTip } from "@/components/HelpTip";
import { Tour, type TourStep } from "@/components/Tour";
import { Button } from "@/components/Button";
import {
  AlertIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  BoxIcon,
  CheckIcon,
  InfoIcon,
  LayersIcon,
  SaveIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";

type StageId = "logo" | "accent" | "copy" | "practice" | "quantity";
type PreviewView = "box-3d" | "box-flat" | "form";

/**
 * First-time coach-marks. Practice managers are non-designers — a tiny
 * tour on first visit teaches them the shape of the screen without
 * making it patronising. Skippable; only ever fires once.
 */
const CUSTOMISE_TOUR_STEPS: TourStep[] = [
  {
    targetId: "customise-controls",
    title: "Edit your brand here",
    body: "Logo, colours, copy — everything updates live in the preview on the right.",
    side: "right",
  },
  {
    targetId: "customise-accent-colour",
    title: "Pick your accent colour",
    body: "Choose a palette or type a hex code. We'll warn you if the contrast is too low for print.",
    side: "right",
  },
  {
    targetId: "customise-preview",
    title: "Live preview",
    body: "Drag to spin the box. The tabs above switch between the 3D view, the flat layout, and the lab form.",
    side: "left",
  },
  {
    targetId: "customise-review-proof",
    title: "Review the proof when you're ready",
    body: "When you're happy with how it looks, head here to approve every artefact and place the order.",
    side: "bottom",
  },
];

/**
 * Customise — step 1 of the brand-and-order flow.
 * Split layout: controls on the left (a staged, single-open accordion
 * 1..5), live preview on the right (3D box / flat / lab form). Brand
 * state is held in Zustand so the preview re-renders instantly on each
 * control change without prop drilling.
 *
 * Only one stage is expanded at a time, so the accent-colour step
 * collapses away once the user moves on — freeing the vertical space
 * the old stacked tab consumed. Version history is a secondary
 * disclosure at the foot of the controls column, not a stage.
 *
 * Lab form preview lands in a follow-up commit (Phase 3 rebuilds it as
 * a pixel-accurate SVG template, so the Phase 1 placeholder here is
 * intentionally minimal).
 */
export function CustomisePage() {
  const { kitId } = useParams();
  const navigate = useNavigate();
  const { data: kit, isLoading } = useKit(kitId);
  const { data: serverBrand } = useMyBrandProfile();
  const { brand, version, setBrand, setAccentPalette } = useBrandStore();
  const updateBrand = useUpdateBrandProfile(serverBrand?.id);

  const [stage, setStage] = useState<StageId>("logo");
  const [showHistory, setShowHistory] = useState(false);
  const [view, setView] = useState<PreviewView>("box-3d");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // Auto-save (debounced 1 s) when brand changes — only the fields the
  // server stores. Soft, doesn't block the UI; status badge tells the user.
  const lastSavedRef = useRef<string>("");
  useEffect(() => {
    if (!serverBrand) return;
    if (serverBrand.locked) return; // server would reject anyway
    const payload = {
      accent: brand.accent,
      secondary: brand.secondary,
      tagline: brand.tagline,
      rhText: brand.rhText,
      activationUrl: brand.activationUrl,
      logoText: brand.logoText ?? null,
    };
    const sig = JSON.stringify(payload);
    if (sig === lastSavedRef.current) return;
    const t = setTimeout(() => {
      setSaveStatus("saving");
      updateBrand.mutate(payload, {
        onSuccess: () => {
          lastSavedRef.current = sig;
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 1500);
        },
        onError: () => setSaveStatus("error"),
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [
    brand.accent, brand.secondary, brand.tagline, brand.rhText,
    brand.activationUrl, brand.logoText, serverBrand?.id, serverBrand?.locked,
  ]);

  if (isLoading) {
    return <div className="mx-auto max-w-7xl px-8 py-10 text-fg-3">Loading kit…</div>;
  }
  if (!kit) {
    return (
      <div className="mx-auto max-w-3xl px-8 py-12 text-center">
        <h1 className="mb-2 text-2xl font-bold">Kit not found</h1>
        <p className="mb-6 text-fg-3">No kit matches the URL.</p>
        <Link to="/catalogue" className="nhsuk-button nhsuk-button--secondary">
          Back to catalogue
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1480px] px-8 py-8">
      <Stepper steps={["Customise", "Review proof", "Order"]} current={0} />

      <header className="mb-5 flex items-end justify-between gap-5">
        <div>
          <div className="mb-1 flex items-center gap-2 text-eyebrow font-semibold uppercase text-fg-mute">
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--studio)" }}
            />
            Step 1 of 3 · Customise
          </div>
          <h1 className="mb-0.5 text-xl font-semibold text-fg-1">{kit.title}</h1>
          <p className="font-mono text-xs text-fg-3">
            {kit.code} · v{version}
            {serverBrand?.locked && (
              <span className="ml-3 rounded bg-[var(--admin-tint)] px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wider text-[var(--admin-dark)]">
                Brand locked
              </span>
            )}
            {saveStatus === "saving" && <span className="ml-2 text-fg-3">· saving…</span>}
            {saveStatus === "saved"  && <span className="ml-2 text-[color:var(--ok)]">· saved</span>}
            {saveStatus === "error"  && <span className="ml-2 text-[color:var(--danger-fg)]">· save failed</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate("/catalogue")}
            leadingIcon={<ArrowLeftIcon />}
          >
            Back
          </Button>
          <Button variant="secondary" leadingIcon={<SaveIcon />}>
            Save draft
          </Button>
          <Button
            data-tour-id="customise-review-proof"
            onClick={() => navigate(`/catalogue/${kit.id}/approval`)}
            trailingIcon={<ArrowRightIcon />}
            className="bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
          >
            Review proof
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-[380px_1fr] items-start gap-5">
        {/* ── CONTROLS ─────────────────────────────────────────────────── */}
        <aside
          data-tour-id="customise-controls"
          className="sticky top-5 overflow-hidden rounded-lg border border-[var(--border-1)] bg-white"
        >
          <div className="max-h-[calc(100vh-140px)] overflow-y-auto">
            <Accordion.Root
              type="single"
              collapsible
              value={stage}
              onValueChange={(v) => v && setStage(v as StageId)}
            >
              <Stage
                id="logo"
                n={1}
                title="Logo"
                done={!!brand.logo}
                help="Prints at 32 mm wide on the kit box. SVG is preferred — it stays sharp at any size. If you only have a raster file, use 300 DPI or higher with a transparent background so no white square shows on the cardboard."
              >
                <LogoDropzone
                  value={brand.logo}
                  onUpload={(logo, error) => {
                    if (error) { alert(error); return; }
                    setBrand({ logo, logoSrc: logo?.src ?? null });
                  }}
                  onRemove={() => setBrand({ logo: null, logoSrc: null })}
                />
              </Stage>

              <Stage
                id="accent"
                n={2}
                title="Accent colour"
                done
                anchorId="customise-accent-colour"
                help="Used on the box accent stripe and the lab form header. Pick a colour that meets WCAG AA on white so the printed artefact stays legible."
              >
                <ColourPicker
                  value={brand.accent}
                  onChange={(c) => setBrand({ accent: c })}
                  onPalette={(p) => setAccentPalette(p.colors[0], p.colors[1])}
                />
              </Stage>

              <Stage
                id="copy"
                n={3}
                title="Tagline & copy"
                done={brand.tagline.trim().length > 0 || brand.rhText.trim().length > 0}
              >
                <Field
                  label="Tagline"
                  hint="under your logo"
                  help="Appears beneath your logo on the kit box. Keep it under 50 characters and use plain English — your practice's full name works well, e.g. &quot;Northshore Regional Health Services&quot;."
                >
                  <input
                    type="text"
                    maxLength={50}
                    value={brand.tagline}
                    onChange={(e) => setBrand({ tagline: e.target.value })}
                    className="field-input"
                  />
                  <div className="field-help">{brand.tagline.length} / 50 characters</div>
                </Field>
                <Field
                  label="Right-hand box text"
                  help="Prints on the right side of the kit lid, opposite the logo. Use it to reassure patients the kit is sanctioned, e.g. &quot;Approved by Northshore Health Hub&quot; or &quot;For patients of …&quot;. Under 48 characters."
                >
                  <input
                    type="text"
                    maxLength={48}
                    value={brand.rhText}
                    onChange={(e) => setBrand({ rhText: e.target.value })}
                    className="field-input"
                  />
                  <div className="field-help">e.g. "Approved by Demo Practice"</div>
                </Field>
              </Stage>

              <Stage
                id="practice"
                n={4}
                title="Practice details"
                done={brand.orgName.trim().length > 0}
              >
                <Field
                  label="Organisation name"
                  required
                  help="Used in legal and contact contexts printed on the lab form. Match how your practice is registered with CQC or NHS Digital (ODS) — not a marketing nickname."
                >
                  <input
                    type="text"
                    value={brand.orgName}
                    onChange={(e) => setBrand({ orgName: e.target.value })}
                    className="field-input"
                  />
                </Field>
                <Field
                  label="Activation URL"
                  hint="printed on lab form"
                  help="Printed on the lab form's &quot;Activate your kit&quot; line. Must be HTTPS and point to a real activation flow your clinic operates. NHS-domain URLs are only permitted if the practice can prove ownership of the domain."
                >
                  <input
                    type="text"
                    value={brand.activationUrl}
                    onChange={(e) => setBrand({ activationUrl: e.target.value })}
                    className="field-input"
                  />
                </Field>
              </Stage>

              <Stage
                id="quantity"
                n={5}
                title="Quantity"
                done
                help="Volume discounts: 100+ kits 5% off, 250+ kits 8% off, 500+ kits 12% off. Thresholds are locked in at order placement and don't combine across orders. A £35 setup fee is charged once per design."
              >
                <Field label="Number of kits">
                  <select
                    value={brand.quantity}
                    onChange={(e) => setBrand({ quantity: parseInt(e.target.value, 10) })}
                    className="field-input"
                  >
                    <option value="25">25 kits · £{(25 * kit.price).toFixed(2)}</option>
                    <option value="50">50 kits · £{(50 * kit.price).toFixed(2)}</option>
                    <option value="100">100 kits · £{(100 * kit.price * 0.95).toFixed(2)} (5% off)</option>
                    <option value="250">250 kits · £{(250 * kit.price * 0.92).toFixed(2)} (8% off)</option>
                    <option value="500">500 kits · £{(500 * kit.price * 0.88).toFixed(2)} (12% off)</option>
                  </select>
                </Field>
                <div className="mt-3 flex gap-2.5 rounded-md border-l-[3px] border-[color:var(--warn)] bg-[#FFFCF4] p-2.5">
                  <AlertIcon width={14} height={14} className="mt-0.5 flex-shrink-0 text-[color:var(--warn)]" />
                  <p className="m-0 text-2xs text-fg-2">
                    Return address, kit code format, patient field labels and barcodes are fixed by lab SOP and UKAS scope. To change locked clinical content, contact your account manager.
                  </p>
                </div>
              </Stage>
            </Accordion.Root>

            {/* Version history — secondary, not part of the staged flow. */}
            <div className="border-t border-[var(--border-1)] p-4">
              <button
                type="button"
                onClick={() => setShowHistory((s) => !s)}
                aria-expanded={showHistory}
                className="inline-flex items-center gap-1.5 text-xxs font-semibold uppercase tracking-wider text-fg-3 hover:text-fg-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
              >
                <ArrowRightIcon
                  width={11}
                  height={11}
                  className={cn("transition-transform", showHistory && "rotate-90")}
                />
                Version history
              </button>
              {showHistory && (
                <div className="mt-3">
                  <p className="m-0 mb-3 text-xs text-fg-3">
                    +CURA Studio keeps the last 50 versions per design. Restoring a version creates a new entry.
                  </p>
                  {[
                    { v: version, label: "Current draft", when: "Just now" },
                    { v: Math.max(version - 1, 0), label: "Previous edit", when: "A few minutes ago" },
                  ]
                    .filter((h) => h.v > 0)
                    .map((h, i) => (
                      <div
                        key={h.v}
                        className={cn(
                          "mb-2 flex items-start gap-2.5 rounded-md border p-2.5 text-xs",
                          i === 0
                            ? "border-[var(--studio-border,var(--studio-tint))] bg-[var(--studio-wash)]"
                            : "border-[var(--border-1)] bg-white",
                        )}
                      >
                        <span
                          className={cn(
                            "grid h-6 w-6 flex-shrink-0 place-items-center rounded-full text-2xs font-bold",
                            i === 0 ? "text-white" : "bg-bg-2 text-fg-3",
                          )}
                          style={i === 0 ? { background: "var(--studio)" } : undefined}
                        >
                          v{h.v}
                        </span>
                        <div>
                          <div className="font-semibold text-fg-1">{h.label}</div>
                          <div className="text-fg-3">{h.when} · You</div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* ── PREVIEW ─────────────────────────────────────────────────── */}
        <div
          data-tour-id="customise-preview"
          className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white"
        >
          <div className="flex items-center justify-between border-b border-[var(--border-1)] bg-bg-1 px-3.5 py-2.5 text-xs">
            <div className="flex gap-1">
              {(
                [
                  { id: "box-3d",   label: "Box · 3D",   Icon: BoxIcon },
                  { id: "box-flat", label: "Box · flat", Icon: LayersIcon },
                  { id: "form",     label: "Lab form",   Icon: InfoIcon },
                ] as const
              ).map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setView(id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
                    view === id
                      ? "border-[var(--border-1)] bg-white font-semibold text-fg-1"
                      : "border-transparent text-fg-3 hover:bg-bg-2 hover:text-fg-1",
                  )}
                >
                  <Icon width={13} height={13} />
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-fg-3">
              <InfoIcon width={12} height={12} />
              <span>Preview shows printed artwork — colours may vary on cardboard.</span>
            </div>
          </div>

          <div className="flex min-h-[540px] items-center justify-center bg-bg-2 p-10">
            {view === "box-3d"   && <BoxMockup brand={brand} view="3d" />}
            {view === "box-flat" && <BoxMockup brand={brand} view="flat" />}
            {view === "form" && <LabFormMockup brand={brand} scale={0.92} />}
          </div>
        </div>
      </div>

      {/* First-time coach-marks. Fires once, then never again. */}
      <Tour steps={CUSTOMISE_TOUR_STEPS} storageKey="cura-studio:customise-tour-seen" />
    </div>
  );
}

/* ── Tiny in-file helpers ──────────────────────────────────────────────── */

/**
 * One numbered stage in the staged controls accordion. The header is a
 * Radix AccordionTrigger so single-open behaviour, keyboard nav and
 * aria wiring come for free. `anchorId` puts a Tour `data-tour-id` on
 * the always-mounted Item wrapper (Radix unmounts collapsed Content,
 * so anchoring there would break coach-marks for closed stages).
 */
function Stage({
  id,
  n,
  title,
  done,
  help,
  anchorId,
  children,
}: {
  id: StageId;
  n: number;
  title: string;
  done?: boolean;
  help?: string;
  anchorId?: string;
  children: React.ReactNode;
}) {
  return (
    <Accordion.Item
      value={id}
      {...(anchorId ? { "data-tour-id": anchorId } : {})}
      className="border-b border-[var(--border-1)] last:border-b-0"
    >
      <Accordion.Header className="m-0">
        <Accordion.Trigger
          className={cn(
            "group flex w-full items-center gap-2.5 px-5 py-3.5 text-left",
            "transition-colors hover:bg-bg-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-focus)]",
            "data-[state=open]:bg-[var(--studio-wash)]",
          )}
        >
          <span
            className={cn(
              "grid h-6 w-6 flex-shrink-0 place-items-center rounded-full text-2xs font-bold",
              done
                ? "text-white"
                : "bg-bg-2 text-fg-3 group-data-[state=open]:text-fg-1",
            )}
            style={done ? { background: "var(--ok)" } : undefined}
          >
            {done ? <CheckIcon width={11} height={11} strokeWidth={2.5} /> : n}
          </span>
          <span className="flex-1 text-xxs font-bold uppercase tracking-wider text-fg-3 group-data-[state=open]:text-fg-1">
            {title}
          </span>
          {help && <HelpTip content={help} />}
          <ArrowRightIcon
            width={12}
            height={12}
            className="flex-shrink-0 text-fg-3 transition-transform group-data-[state=open]:rotate-90"
          />
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content className="px-5 pb-5 pt-1">
        {children}
      </Accordion.Content>
    </Accordion.Item>
  );
}

function Field({
  label,
  hint,
  help,
  required,
  children,
}: {
  label: string;
  hint?: string;
  help?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="mb-1.5 block text-sm font-semibold text-fg-1">
        <span className="inline-flex items-center gap-1.5">
          <span>{label}</span>
          {required && <span className="text-[color:var(--danger)]">*</span>}
          {help && <HelpTip content={help} />}
        </span>
        {hint && <span className="ml-1.5 text-xs font-normal text-fg-3">{hint}</span>}
      </label>
      {children}
    </div>
  );
}
